/**
 * A FICHA SÓ-LEITURA DE UM SERVIDOR — o 👁 das telas de servidores.
 *
 * Um componente para os DOIS perfis (averbadora e prefeitura). Eles têm tipos de
 * linha diferentes (`AdminServidor` × `PrefeituraServidor`) e chamadas de SDK
 * diferentes, então o que entra aqui já vem normalizado: a config de campos, um
 * mapa de valores e o `ServidorDetalhe` da rota. Duas cópias divergiriam na
 * primeira correção feita só de um lado — foi o que aconteceu com a ordem das
 * colunas antes de a config passar a mandar nas duas telas.
 *
 * POR QUE SÓ-LEITURA E NÃO O MODAL DE EDIÇÃO TRAVADO. Conferir um servidor era
 * um caminho de escrita: abria o formulário. Aqui não há input nenhum — são
 * pares rótulo/valor. Sem campo editável não há o que salvar por engano.
 *
 * O cadastro vem da LINHA da listagem, não de uma busca nova: ela já carrega
 * tudo, inclusive os campos custom. A rota de detalhe traz só o que a linha não
 * tem — margem e contratos.
 *
 * A MARGEM É POR MODALIDADE, e o quadro diz isso na cara. Empréstimo, cartão
 * consignado e cartão benefício têm tetos próprios, vindos da REGRA do órgão
 * (ADR-0012) e não de constante. Antes isso era uma frase em cinza sob quatro
 * números, e o "disponível" era lido como a margem inteira do servidor.
 */
import { useId, useState, type ReactNode } from "react";
import {
  ContratosTable,
  Button,
  Modal,
  Pill,
  Skeleton,
  fmtBRL,
  fmtDataISO,
} from "@atlas/ui/web";
import type { DescontoDoServidor, HistoricoPagamentosContrato, ServidorCampoConfig, ServidorDetalhe } from "@atlas/sdk";
import { CredorTipoPill } from "../lib/credor-tipo";
import { expiracaoDoContrato } from "../lib/expiracao-contrato";
import { HistoricoPagamentosContratoModal } from "./HistoricoPagamentosContratoModal";
// Os rotulos de balde, destino e tipo de servico sao UMA copia so', a da tela de
// rubricas (#270): a ficha nao inventa nome. Mesmo precedente do EnsaioPainel.
import { DESTINO_LABEL, PRODUTO_LABEL, rotuloTipoServico } from "../routes/averbadora/motor-rubricas-labels";

const fmtCpf = (cpf: string) => {
  const d = (cpf ?? "").replace(/\D/g, "");
  return d.length === 11 ? `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}` : (cpf || "—");
};
const fmtTel = (tel?: string) => {
  const d = (tel ?? "").replace(/\D/g, "");
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return d || "—";
};

const TRAVESSAO = <span style={{ color: "var(--text-dim)" }}>—</span>;

/** De onde vem cada desconto da secao "Descontos". */
const ROTULO_ORIGEM: Record<DescontoDoServidor["origem"], string> = {
  contrato: "Contrato Atlas",
  legado: "Contrato legado",
  herdada: "Dívida herdada",
  consignacao: "Consignação",
  holerite: "Contracheque",
};

/** 0.0833 -> "8,33%". A base e' o salario liquido da folha (ADR-0012). */
const fmtPercentual = (fracao: number) => `${(fracao * 100).toFixed(2).replace(".", ",")}%`;

/**
 * Um valor de servidor renderizado pelo TIPO declarado na config.
 *
 * Mesma régua da tabela (`servidores/visualizar.tsx`): vazio e zero viram "—"
 * (IBGE 0 = não veio no CSV, salário 0 = não informado), status e situação
 * funcional viram `Pill`, e data ISO vira DD/MM/AAAA. Se a tabela e a ficha
 * formatassem diferente, o operador leria dois valores para o mesmo campo.
 */
export function valorDoCampo(
  campo: { key: string; tipo: ServidorCampoConfig["tipo"] },
  v: string | number | null | undefined,
): ReactNode {
  if (v == null || v === "") return TRAVESSAO;
  if ((campo.tipo === "numero" || campo.tipo === "moeda") && Number(v) === 0) return TRAVESSAO;
  if (campo.key === "cpf") return fmtCpf(String(v));
  if (campo.key === "telefone" || campo.tipo === "telefone") return fmtTel(String(v));
  if (campo.key === "status") {
    const st = String(v);
    return <Pill variant={st === "ativo" ? "averbado" : st === "bloqueado" ? "rejeitada" : "expirado"}>{st}</Pill>;
  }
  if (campo.key === "situacaoFuncional") {
    const situ = String(v).trim().toUpperCase();
    if (!situ) return <span style={{ color: "var(--text-dim)", fontSize: 12 }}>NÃO INFORMADO</span>;
    const variant: "expirado" | "averbado" | "pendente" =
      /desligado|aposentad/i.test(situ) ? "expirado"
      : /afastad|licenc|ferias|cedid/i.test(situ) ? "pendente"
      : "averbado";
    return <Pill variant={variant}>{situ}</Pill>;
  }
  switch (campo.tipo) {
    case "moeda":
      return fmtBRL(typeof v === "number" ? v : Number(v) || 0);
    case "data":
      return fmtDataISO(String(v));
    default:
      return String(v);
  }
}

// ---------------------------------------------------------------- ícones
//
// SVG inline, e não emoji: emoji muda de desenho e de cor por sistema
// operacional, e aqui o ícone é parte da moldura, não conteúdo. `currentColor`
// deixa cada um herdar a cor de quem o põe na tela.

const svg = { width: 18, height: 18, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };

const IconePessoa = () => (
  <svg {...svg} aria-hidden="true"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
);
const IconeMargem = () => (
  <svg {...svg} aria-hidden="true"><path d="M3 3v18h18" /><rect x="7" y="12" width="3" height="6" /><rect x="12" y="8" width="3" height="10" /><rect x="17" y="4" width="3" height="14" /></svg>
);
const IconeContratos = () => (
  <svg {...svg} aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M8 13h8M8 17h5" /></svg>
);
const IconeDescontos = () => (
  <svg {...svg} aria-hidden="true"><path d="M4 4h16v16l-3-2-2.5 2-2.5-2-2.5 2L7 18l-3 2z" /><path d="M8 9h8M8 13h5" /></svg>
);
const IconeCarteira = () => (
  <svg {...svg} aria-hidden="true"><rect x="2" y="6" width="20" height="14" rx="2" /><path d="M2 10h20" /><circle cx="17" cy="15" r="1.2" /></svg>
);
const IconeChevron = () => (
  <svg {...svg} width={16} height={16} aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
);
const IconeInfo = () => (
  <svg {...svg} width={13} height={13} aria-hidden="true"><circle cx="12" cy="12" r="10" /><path d="M12 16v-5M12 8h.01" /></svg>
);

// ---------------------------------------------------------------- moldura

/**
 * Um cartão por assunto, e SÓ UM aberto por vez.
 *
 * Empilhadas e sempre abertas, as três seções passavam da altura da tela numa
 * janela baixa — a margem aparecia cortada no meio da tabela de modalidades, sem
 * nada avisando que havia mais. Com um painel por vez o conteúdo cabe, e o que
 * está fechado continua anunciado pelo próprio cabeçalho.
 *
 * O painel fechado NÃO fica no DOM escondido: leitor de tela e busca da página
 * anunciariam conteúdo que o olho não vê.
 *
 * `minWidth: 0` aqui e no `Campo` NÃO é decoração. Item de grid nasce com
 * `min-width: auto`, que resolve para a largura de min-content. Um e-mail longo
 * sem ponto de quebra ("karina.barbosa.gomes.909971@exemplo.com") tem min-content
 * GRANDE, então a trilha cresce além de `1fr` e o conteúdo vaza para fora da
 * caixa do diálogo — que tem `maxWidth` e não acompanha. Zerar o mínimo devolve
 * à trilha o direito de encolher, e o `overflowWrap` do Campo dá onde quebrar.
 */
function Secao({ titulo, icone, aberta, onAlternar, children }: {
  titulo: string;
  icone: ReactNode;
  aberta: boolean;
  onAlternar: () => void;
  children: ReactNode;
}) {
  const painelId = useId();
  return (
    <section
      style={{
        minWidth: 0,
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-lg)",
        background: "var(--bg-elev)",
        overflow: "hidden",
      }}
    >
      <h3 style={{ margin: 0 }}>
        <button
          type="button"
          onClick={onAlternar}
          aria-expanded={aberta}
          aria-controls={aberta ? painelId : undefined}
          style={{
            width: "100%", display: "flex", alignItems: "center", gap: 10,
            padding: "12px 16px", background: "var(--bg-elev-2)",
            border: "none", borderBottom: aberta ? "1px solid var(--border)" : "none",
            color: "var(--text)", font: "inherit", fontSize: 15, fontWeight: 600,
            cursor: "pointer", textAlign: "left",
          }}
        >
          <span style={{ display: "grid", placeItems: "center", color: "var(--gold-500)", flexShrink: 0 }}>{icone}</span>
          <span style={{ flex: 1, minWidth: 0 }}>{titulo}</span>
          <span
            style={{
              display: "grid", placeItems: "center", flexShrink: 0, color: "var(--text-dim)",
              transform: aberta ? "rotate(180deg)" : "none", transition: "transform .15s var(--ease-out)",
            }}
          >
            <IconeChevron />
          </span>
        </button>
      </h3>
      {aberta ? <div id={painelId} style={{ padding: 16, minWidth: 0 }}>{children}</div> : null}
    </section>
  );
}

function Campo({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 12, color: "var(--text-dim)", marginBottom: 3, overflowWrap: "anywhere" }}>{rotulo}</div>
      {/* `anywhere` e não `break-word`: e-mail e endereço são uma "palavra" só
          para o navegador, e `break-word` só age depois de tentar caber inteira
          — que é justamente o caso que estoura. */}
      <div style={{ fontSize: 14, fontWeight: 500, overflowWrap: "anywhere" }}>{children}</div>
    </div>
  );
}

/** Três colunas na largura `xl`, e menos conforme a caixa encolhe. */
function Grade({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: "grid",
        // `min(260px, 100%)` e não `260px` cru: numa tela estreita (o diálogo
        // ocupa 100% menos a margem) uma trilha de mínimo fixo não consegue
        // encolher e empurra a grade para fora.
        gridTemplateColumns: "repeat(auto-fit, minmax(min(220px, 100%), 1fr))",
        gap: "18px 24px",
      }}
    >
      {children}
    </div>
  );
}

/**
 * Um dos três totais do topo da margem. `destaque` é o que decide contratação;
 * `alerta` troca o dourado pelo vermelho quando a margem foi excedida (#352).
 */
function Totalizador({
  rotulo,
  valor,
  destaque = false,
  alerta = false,
}: {
  rotulo: string;
  valor: string;
  destaque?: boolean;
  alerta?: boolean;
}) {
  const cor = alerta ? "var(--danger-500)" : "var(--gold-500)";
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "12px 16px",
        borderRadius: "var(--radius-md)",
        border: destaque ? `1px solid ${cor}` : "1px solid transparent",
        background: destaque ? "var(--bg-elev-2)" : "transparent",
        boxShadow: destaque && !alerta ? "var(--shadow-gold)" : undefined,
        minWidth: 0,
      }}
    >
      {destaque ? <span style={{ color: cor, display: "grid", placeItems: "center" }}><IconeCarteira /></span> : null}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12.5, color: destaque ? cor : "var(--text-muted)" }}>{rotulo}</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: destaque ? cor : "var(--text)", overflowWrap: "anywhere" }}>
          {valor}
        </div>
      </div>
    </div>
  );
}

function ThM({ children, align = "left" }: { children: ReactNode; align?: "left" | "right" }) {
  return (
    <th style={{ textAlign: align, padding: "9px 14px", fontSize: 12, fontWeight: 600, color: "var(--text-muted)", whiteSpace: "nowrap" }}>
      {children}
    </th>
  );
}
function TdM({
  children,
  forte = false,
  align = "left",
  cor,
}: {
  children: ReactNode;
  forte?: boolean;
  align?: "left" | "right";
  cor?: string;
}) {
  return (
    <td style={{ textAlign: align, padding: "10px 14px", fontSize: 13.5, color: cor ?? (forte ? "var(--text)" : "var(--text-muted)"), fontWeight: forte ? 600 : 400, whiteSpace: "nowrap" }}>
      {children}
    </td>
  );
}

/**
 * A margem, modalidade por modalidade — e o consolidado somado a partir delas.
 *
 * Os totais do topo são SOMA das linhas, calculada aqui: a rota não manda total
 * pronto, de propósito. Dois lugares guardando o mesmo número divergem no dia em
 * que um balde mudar de teto.
 *
 * Somar os três baldes é legítimo para uma visão consolidada, mas NÃO é margem
 * de nada: ninguém contrata R$ X de "margem total". Por isso o realce fica no
 * empréstimo, que é o balde que decide a maior parte das operações, e o
 * consolidado vem em corpo menor.
 *
 * Margem EXCEDIDA (#352): o `saldo` negativo de uma modalidade vira o número
 * em vermelho, com um aviso acima da tabela. Antes a ficha mostrava R$ 0,00 de
 * disponível, e só a conta de cabeça revelava que o servidor devia margem.
 */
export function QuadroMargem({
  margens,
  compartilhada,
}: {
  margens: ServidorDetalhe["margens"];
  /** Saldo único informado pelo órgão: o topo mostra ele, e não a soma. */
  compartilhada?: ServidorDetalhe["margemCompartilhada"];
}) {
  // Com saldo compartilhado as três linhas mostram o MESMO saldo: somá-las
  // triplicava a margem (R$ 23 mil para quem tem R$ 7,7 mil). O topo usa o saldo
  // único que a rota manda; sem ele, cada produto tem teto próprio e soma.
  const soma = (campo: "total" | "comprometida" | "disponivel") =>
    compartilhada ? compartilhada[campo] : margens.reduce((acc, m) => acc + m[campo], 0);
  const emprestimo = margens.find((m) => m.tipo === "EMPRESTIMO");
  // Balde com margem informada: a Comprometida vem da foto da instituicao, e as
  // duas colunas de origem seguem a regra — nao precisam somar a ela.
  const algumInformado = margens.some((m) => m.fonte === "informada");
  // `saldo` ausente (API anterior) ou nulo (impedimento) nao e' excesso.
  const excesso = (saldo: number | null | undefined) => (saldo != null && saldo < 0 ? -saldo : null);
  const excessoEmprestimo = excesso(emprestimo?.saldo);
  const excedidas = margens.filter((m) => excesso(m.saldo) != null);
  const excessoUnico = compartilhada ? excesso(compartilhada.saldo) : null;

  return (
    <>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(190px, 100%), 1fr))",
          gap: 8,
          marginBottom: 14,
        }}
      >
        <Totalizador rotulo="Margem total" valor={fmtBRL(soma("total"))} />
        <Totalizador rotulo="Total comprometido" valor={fmtBRL(soma("comprometida"))} />
        {excessoEmprestimo != null ? (
          <Totalizador rotulo="Margem excedida no empréstimo" valor={fmtBRL(excessoEmprestimo)} destaque alerta />
        ) : (
          <Totalizador
            rotulo="Disponível para empréstimo"
            valor={fmtBRL(emprestimo?.disponivel ?? 0)}
            destaque
          />
        )}
      </div>

      {excessoUnico != null || excedidas.length > 0 ? (
        <p
          role="alert"
          style={{
            margin: "0 0 14px",
            padding: "10px 14px",
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--danger-500)",
            color: "var(--danger-500)",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {excessoUnico != null
            ? `Margem excedida em ${fmtBRL(excessoUnico)}: as parcelas já passam do saldo informado pelo órgão.`
            : `Margem excedida: ${excedidas
                .map((m) => `${PRODUTO_LABEL[m.tipo]} em ${fmtBRL(excesso(m.saldo)!)}`)
                .join("; ")}.`}
        </p>
      ) : null}

      {/* A tabela tem 4 colunas de `nowrap`; abaixo de ~460px ela não cabe.
         Rolar DENTRO da moldura é melhor que espremer a coluna de valor até o
         número quebrar em duas linhas. */}
      <div style={{ border: "1px solid var(--border)", borderRadius: "var(--radius-md)", overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 620 }}>
          <thead>
            <tr style={{ background: "var(--bg-elev-2)" }}>
              <ThM>Modalidade</ThM>
              <ThM align="right">Margem</ThM>
              <ThM align="right">Comprometida</ThM>
              {/* De onde vem o comprometido (#275): contrato e divida herdada, e
                  consignacao facultativa que a regra do orgao manda para o balde. */}
              <ThM align="right">De crédito</ThM>
              <ThM align="right">De facultativa</ThM>
              <ThM align="right">Disponível</ThM>
            </tr>
          </thead>
          <tbody>
            {margens.map((m) => (
              <tr key={m.tipo} style={{ borderTop: "1px solid var(--border)" }}>
                <TdM forte>{PRODUTO_LABEL[m.tipo]}</TdM>
                <TdM align="right">{fmtBRL(m.total)}</TdM>
                <TdM align="right">{fmtBRL(m.comprometida)}</TdM>
                <TdM align="right">{m.credito > 0 ? fmtBRL(m.credito) : TRAVESSAO}</TdM>
                <TdM align="right">{m.facultativa > 0 ? fmtBRL(m.facultativa) : TRAVESSAO}</TdM>
                {/* Excedida, a coluna mostra o saldo negativo em vermelho, e não o zero. */}
                {excesso(m.saldo) != null ? (
                  <TdM align="right" forte cor="var(--danger-500)">{fmtBRL(m.saldo!)}</TdM>
                ) : (
                  <TdM align="right" forte>{fmtBRL(m.disponivel)}</TdM>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p style={{ display: "flex", alignItems: "center", gap: 6, margin: "10px 0 0", fontSize: 12, color: "var(--text-dim)" }}>
        <IconeInfo />
        {compartilhada
          ? "A margem vem do arquivo do órgão e é um saldo só para os três produtos: o que um compromete sai do disponível dos outros. Por isso o total não é a soma das linhas."
          : "Os tetos vêm da regra do órgão e podem ter limite somado entre modalidades — por isso comprometida + disponível nem sempre fecha com a margem. O total é a soma das três, e não margem contratável de nenhuma delas. A parte de facultativa é a que a regra do órgão manda para o balde: plano de saúde ou odontológico só pesa aqui quando a regra diz."}
      </p>
      {algumInformado ? (
        <p style={{ margin: "6px 0 0", fontSize: 12, color: "var(--text-dim)" }}>
          Com margem informada pelo órgão, a Comprometida vem do saldo da instituição; De crédito e De facultativa
          seguem a regra e podem não somar a ela.
        </p>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------- descontos

const subtotalCentavos = (itens: DescontoDoServidor[]) => itens.reduce((s, d) => s + d.valorCentavos, 0);

/**
 * Todos os descontos do servidor, em tres grupos (#275): o que a lei impoe, as
 * consignacoes facultativas e o credito. Ate' agora a ficha so' listava contrato;
 * a divida herdada e a consignacao pesavam sem aparecer.
 *
 * Os obrigatorios saem do holerite gravado (#273). Servidor sem holerite: a
 * tela diz isso, e nao desenha "R$ 0,00" — zero pareceria "este servidor nao
 * tem", e a verdade e' "ainda nao sabemos". Eles reduzem a BASE da margem e
 * nao ocupam margem: ficam fora do comprometimento total.
 *
 * Tabela a mao, como o `QuadroMargem`: sao tres grupos curtos de um servidor so'.
 */
function QuadroDescontos({ detalhe }: { detalhe: ServidorDetalhe }) {
  const { descontos, obrigatoriosDisponiveis, holeriteCompetencia, comprometimentoTotalCentavos, percentualDaBase } =
    detalhe;
  const mesDoHolerite = holeriteCompetencia ? `${holeriteCompetencia.slice(4)}/${holeriteCompetencia.slice(0, 4)}` : null;
  const grupos: { chave: string; titulo: string; sub?: string; itens: DescontoDoServidor[]; vazio: string }[] = [
    {
      chave: "obrigatorios",
      titulo: "Obrigatórios",
      ...(mesDoHolerite ? { sub: `contracheque de ${mesDoHolerite}` } : {}),
      itens: descontos.obrigatorios,
      vazio: obrigatoriosDisponiveis
        ? "Nenhuma rubrica deste contracheque está classificada como desconto obrigatório."
        : "Ainda não disponíveis: não há contracheque gravado para este servidor. Eles aparecem a partir do próximo retorno da folha.",
    },
    { chave: "facultativas", titulo: "Facultativas", itens: descontos.facultativas, vazio: "Nenhum desconto neste grupo." },
    { chave: "credito", titulo: "Crédito", itens: descontos.credito, vazio: "Nenhum desconto neste grupo." },
  ];
  return (
    <>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(190px, 100%), 1fr))",
          gap: 8,
          marginBottom: 14,
        }}
      >
        <Totalizador rotulo="Comprometimento total" valor={fmtBRL(comprometimentoTotalCentavos / 100)} destaque />
        <Totalizador
          rotulo="Sobre o salário líquido"
          valor={percentualDaBase == null ? "—" : fmtPercentual(percentualDaBase)}
        />
      </div>
      {descontos.obrigatorios.length > 0 ? (
        <p style={{ margin: "0 0 12px", fontSize: 12, color: "var(--text-dim)" }}>
          Os obrigatórios reduzem a base da margem e não entram no comprometimento total.
        </p>
      ) : null}

      <div style={{ border: "1px solid var(--border)", borderRadius: "var(--radius-md)", overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
          <thead>
            <tr style={{ background: "var(--bg-elev-2)" }}>
              <ThM>Rubrica</ThM>
              <ThM>Descrição</ThM>
              <ThM>Serviço</ThM>
              <ThM>Consignatária</ThM>
              <ThM>Destino</ThM>
              <ThM>Origem</ThM>
              <ThM align="right">Valor</ThM>
            </tr>
          </thead>
          {grupos.map((g) => (
            <tbody key={g.chave} aria-label={g.titulo}>
              <tr style={{ borderTop: "1px solid var(--border)", background: "var(--bg-elev-2)" }}>
                <td colSpan={6} style={{ padding: "8px 14px", fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--text-muted)" }}>
                  {g.titulo} <span style={{ fontWeight: 400 }}>({g.itens.length})</span>
                  {g.sub ? (
                    <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}> · {g.sub}</span>
                  ) : null}
                </td>
                <td style={{ padding: "8px 14px", textAlign: "right", fontSize: 13, fontWeight: 600 }}>
                  {g.itens.length > 0 ? fmtBRL(subtotalCentavos(g.itens) / 100) : null}
                </td>
              </tr>
              {g.itens.length === 0 ? (
                <tr style={{ borderTop: "1px solid var(--border)" }}>
                  <td colSpan={7} style={{ padding: "10px 14px", fontSize: 13, color: "var(--text-dim)" }}>{g.vazio}</td>
                </tr>
              ) : (
                g.itens.map((d, i) => (
                  <tr key={`${g.chave}-${d.rubrica}-${i}`} style={{ borderTop: "1px solid var(--border)" }}>
                    <TdM forte>{d.rubrica || "—"}</TdM>
                    <TdM>{d.descricao}</TdM>
                    <TdM>{d.tipoServico ? rotuloTipoServico(d.tipoServico) : TRAVESSAO}</TdM>
                    <TdM>
                      {d.consignatariaNome ?? TRAVESSAO}
                      {d.credorFinalNome ? (
                        <div style={{ fontSize: 11.5, color: "var(--text-dim)" }}>Credor final: {d.credorFinalNome}</div>
                      ) : null}
                    </TdM>
                    <TdM>{DESTINO_LABEL[d.destino]}</TdM>
                    <TdM>{ROTULO_ORIGEM[d.origem]}</TdM>
                    <TdM align="right" forte>{fmtBRL(d.valorCentavos / 100)}</TdM>
                  </tr>
                ))
              )}
            </tbody>
          ))}
        </table>
      </div>

      <p style={{ display: "flex", alignItems: "flex-start", gap: 6, margin: "10px 0 0", fontSize: 12, color: "var(--text-dim)" }}>
        <IconeInfo />
        <span>
          O total é a soma do que está listado, e inclui o que não ocupa margem. O destino é o que a regra do órgão diz
          para o serviço; com margem informada pelo órgão, o saldo da instituição já contém esses descontos e o
          percentual sobre o salário não se aplica.
        </span>
      </p>
    </>
  );
}

// ---------------------------------------------------------------- ficha

export interface ServidorDetalheModalProps {
  /** Nome do servidor — vira o rótulo acessível do diálogo. */
  nome: string;
  /** Ordem e rótulos vêm da config que a averbadora definiu; é a MESMA que
   *  ordena a tabela, então ficha e tabela nunca discordam. */
  campos: { key: string; label: string; tipo: ServidorCampoConfig["tipo"] }[];
  /** Valor por key, incluindo os `custom_*`. */
  valores: Record<string, string | number | null | undefined>;
  detalhe: ServidorDetalhe | undefined;
  carregando: boolean;
  /** Falhou buscar margem/contratos. O cadastro continua legível — ele veio da
   *  linha, não da rota; dizer "não foi possível carregar" a ficha inteira
   *  esconderia dado que já está na mão. */
  erro?: boolean;
  /** Carrega o histórico de pagamentos de uma ADF. Quando vem, a linha de
   *  contrato fica clicável e abre o MESMO modal da tela de contratos. Vem por
   *  injeção porque a rota difere por perfil (`admin` × `prefeitura`), como já
   *  acontece com o resto do que entra aqui. */
  carregarHistorico?: (adf: string) => Promise<HistoricoPagamentosContrato>;
  onClose: () => void;
}

export function ServidorDetalheModal({
  nome, campos, valores, detalhe, carregando, erro = false, carregarHistorico, onClose,
}: ServidorDetalheModalProps) {
  const [adfAberta, setAdfAberta] = useState<string | null>(null);
  // Um painel por vez. `null` = todos fechados, que é um estado útil: numa
  // janela baixa o operador fecha tudo e lê só o cabeçalho com nome e matrícula.
  const [painel, setPainel] = useState<"dados" | "margem" | "descontos" | "contratos" | null>("dados");
  const alternar = (qual: "dados" | "margem" | "descontos" | "contratos") =>
    setPainel((atual) => (atual === qual ? null : qual));

  // UM diálogo por vez: a ficha SAI e o detalhe entra, em vez de empilhar.
  // Empilhar brigaria de três formas — o `Modal` do design system prende o foco
  // e captura o ESC no document (o de baixo responderia primeiro), e o modal de
  // pagamentos ainda usa o `Modal` de `prefeitura/_ui`, cujo z-index é menor:
  // ele abriria ATRÁS da ficha. Fechar o detalhe devolve a ficha, que é
  // só-leitura e não perde estado nenhum ao remontar.
  if (adfAberta && carregarHistorico) {
    return (
      <HistoricoPagamentosContratoModal
        adf={adfAberta}
        carregar={() => carregarHistorico(adfAberta)}
        operacional
        onClose={() => setAdfAberta(null)}
      />
    );
  }

  const matricula = valores.matricula;

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow="Detalhes do servidor"
      title={nome}
      subtitle={matricula ? <>Matrícula <b style={{ color: "var(--text)" }}>{String(matricula)}</b></> : null}
      showClose
      size="xl"
      footer={<Button variant="ghost" onClick={onClose}>Fechar</Button>}
    >
      {/* Quem rola agora é o corpo do `Modal`, que também limita a caixa à
          altura da tela. Aqui só empilha — um `maxHeight` em `vh` a mais nesta
          camada brigaria com aquele e voltaria a cortar conteúdo. */}
      <div style={{ display: "grid", gap: 12, alignContent: "start", minWidth: 0 }}>
        <Secao titulo="Dados funcionais" icone={<IconePessoa />} aberta={painel === "dados"} onAlternar={() => alternar("dados")}>
          <Grade>
            {campos.map((c) => (
              <Campo key={c.key} rotulo={c.label}>{valorDoCampo(c, valores[c.key])}</Campo>
            ))}
          </Grade>
        </Secao>

        <Secao titulo="Margem total do servidor" icone={<IconeMargem />} aberta={painel === "margem"} onAlternar={() => alternar("margem")}>
          {carregando ? <Skeleton /> : erro || !detalhe ? (
            <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>
              Não foi possível carregar a margem.
            </p>
          ) : (
            <QuadroMargem margens={detalhe.margens} compartilhada={detalhe.margemCompartilhada} />
          )}
        </Secao>

        <Secao titulo="Descontos" icone={<IconeDescontos />} aberta={painel === "descontos"} onAlternar={() => alternar("descontos")}>
          {carregando ? <Skeleton /> : erro || !detalhe ? (
            <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>
              Não foi possível carregar os descontos.
            </p>
          ) : (
            <QuadroDescontos detalhe={detalhe} />
          )}
        </Secao>

        <Secao titulo="Contratos" icone={<IconeContratos />} aberta={painel === "contratos"} onAlternar={() => alternar("contratos")}>
          <ContratosTable
            /* Mesmo par nome+pill da tela de contratos. Numa base so' de
               divida herdada e' o unico "quem" que existe: convenio ali e'
               vazio por construcao. */
            showCredor
            rows={(detalhe?.contratos ?? []).map((ct) => ({
              ...ct,
              // Contrato averbado nao tem `expiracao` (so' reserva tem): sem
              // isto a coluna ficava "—/—/—" para todos (#356).
              expiracao: expiracaoDoContrato(ct),
              onClick: carregarHistorico ? () => setAdfAberta(ct.adf) : undefined,
              credor: (
                <>
                  <div>{ct.bancoNome}</div>
                  <CredorTipoPill tipo={ct.credorTipo} />
                </>
              ),
            }))}
            loading={carregando}
            emptyState={erro
              ? "Não foi possível carregar os contratos."
              : "Este servidor não tem contrato nesta folha."}
          />
        </Secao>
      </div>
    </Modal>
  );
}
